import { useState, useEffect, useCallback, useMemo } from 'react';

// Debounce hook for search and filters
export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Throttle hook for scroll events
export const useThrottle = (callback, delay) => {
  const [throttledCallback, setThrottledCallback] = useState(callback);

  useEffect(() => {
    let timeoutId;
    let lastExecTime = 0;

    const throttled = (...args) => {
      const currentTime = Date.now();
      
      if (currentTime - lastExecTime > delay) {
        callback(...args);
        lastExecTime = currentTime;
      } else {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          callback(...args);
          lastExecTime = Date.now();
        }, delay - (currentTime - lastExecTime));
      }
    };

    setThrottledCallback(() => throttled);
  }, [callback, delay]);

  return throttledCallback;
};

// Virtual scrolling hook for large lists
export const useVirtualScroll = (items, itemHeight, containerHeight) => {
  const [scrollTop, setScrollTop] = useState(0);
  
  const visibleItems = useMemo(() => {
    const startIndex = Math.floor(scrollTop / itemHeight);
    const endIndex = Math.min(
      startIndex + Math.ceil(containerHeight / itemHeight) + 1,
      items.length
    );
    
    return {
      items: items.slice(startIndex, endIndex),
      startIndex,
      endIndex,
      totalHeight: items.length * itemHeight,
      offsetY: startIndex * itemHeight,
    };
  }, [items, itemHeight, containerHeight, scrollTop]);

  const handleScroll = useCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, []);

  return {
    visibleItems,
    handleScroll,
  };
};

// Image lazy loading hook
export const useImageLazyLoad = (src, options = {}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isInView, setIsInView] = useState(false);

  const { threshold = 0.1, rootMargin = '50px' } = options;

  useEffect(() => {
    if (!isInView) return;

    const img = new Image();
    
    img.onload = () => {
      setImageSrc(src);
      setIsLoaded(true);
    };
    
    img.onerror = () => {
      setIsError(true);
    };
    
    img.src = src;
  }, [src, isInView]);

  const ref = useCallback((node) => {
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
  }, [threshold, rootMargin]);

  return {
    ref,
    imageSrc,
    isLoaded,
    isError,
    isInView,
  };
};

// Performance monitoring hook
export const usePerformanceMonitor = (componentName) => {
  const [renderTime, setRenderTime] = useState(0);
  const [renderCount, setRenderCount] = useState(0);

  useEffect(() => {
    const startTime = performance.now();
    
    return () => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      setRenderTime(duration);
      setRenderCount(prev => prev + 1);
      
      // Log performance in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`${componentName} render time: ${duration.toFixed(2)}ms`);
      }
    };
  });

  return { renderTime, renderCount };
};

// Memoization hook for expensive calculations
export const useMemoizedValue = (factory, deps) => {
  return useMemo(factory, deps);
};

// Batch state updates hook
export const useBatchedUpdates = () => {
  const [updates, setUpdates] = useState([]);
  const [isBatching, setIsBatching] = useState(false);

  const batchUpdate = useCallback((updateFn) => {
    if (isBatching) {
      setUpdates(prev => [...prev, updateFn]);
    } else {
      setIsBatching(true);
      setUpdates([updateFn]);
      
      // Process updates in next tick
      setTimeout(() => {
        setUpdates(prev => {
          prev.forEach(update => update());
          return [];
        });
        setIsBatching(false);
      }, 0);
    }
  }, [isBatching]);

  return batchUpdate;
};

export default {
  useDebounce,
  useThrottle,
  useVirtualScroll,
  useImageLazyLoad,
  usePerformanceMonitor,
  useMemoizedValue,
  useBatchedUpdates,
};
